import { createReducer, on } from "@ngrx/store";
import { authServiceActions } from "@pcp/auth";
import { ClassroomState } from "../../public/store/classroomState";
import type { NavigationPreviewNextSlideData } from "../../public/store/classroomState";
import type { MinimumSlideInformation } from "@pcp/core/interfaces";
import * as LiveLessonActions from "../../public/store/live-lesson.actions";
import {
    lessonActionsOpenScreen,
    restoreLessonActions,
    startLessonActions,
    stopLessonActions,
} from "../../public/store/lesson.actions";
import { classroomConnectionActions } from "../../public/state/classroom.actions";
import { classroomLifecycleEvents, classroomReceiverActions } from "../../public/store/classroom-sync.actions";
import { yjsStateActions } from "./yjs-state.actions";

export const lessonFeatureKey = 'classroom';

const NAVIGATION_PREVIEW_BASIC_SLIDES_DEFAULT = '{"slides":[],"hasParentSlide":false}';
const NAVIGATION_PREVIEW_NEXT_SLIDES_DEFAULT = 'null';

const NAVIGATION_PREVIEW_DEFAULTS: Pick<ClassroomState,
    'navigationPreviewBasicSlides' | 'navigationPreviewNextSlides' |
    'navigationPreviewBasicSlideList' | 'navigationPreviewHasParent' | 'navigationPreviewNextSlideData'
> = {
    navigationPreviewBasicSlides: NAVIGATION_PREVIEW_BASIC_SLIDES_DEFAULT,
    navigationPreviewNextSlides: NAVIGATION_PREVIEW_NEXT_SLIDES_DEFAULT,
    navigationPreviewBasicSlideList: [] as MinimumSlideInformation[],
    navigationPreviewHasParent: false,
    navigationPreviewNextSlideData: null as NavigationPreviewNextSlideData | null,
};

export const initialState: ClassroomState = {
    paused: false,
    started: false,
    liveLessonList: [],
    isShowingStudentRegistrationCode: false,
    isLessonStopPending: false,
    pauseImageUrl: null,
    ...NAVIGATION_PREVIEW_DEFAULTS,
};

export const lessonReducer = createReducer(
    initialState,
    on(authServiceActions.userHasBeenLoggedOut, (): ClassroomState => initialState),
    on(
        restoreLessonActions.restoreLessonStateSuccess,
        (state, { lesson, lessonType }): ClassroomState => ({
            ...state,
            started: true,
            // NOTE: Do NOT set synced props (paused, isShowingStudentRegistrationCode, etc.)
            // Those come from Yjs after hydration (Redis state).
            activeLesson: {
                ...lesson,
            },
            liveLessonId: lessonType === 'LiveLesson' ? lesson.lessonId : undefined,
        })
    ),
    on(
        startLessonActions.startLesson,
        (state): ClassroomState => ({
            ...state,
            isLoadingLessonCode: true,
            started: false,
            startedAt: undefined,
        })
    ),
    on(
        startLessonActions.startLessonFailed,
        lessonActionsOpenScreen.openAdditionalBeamerScreenFailed,
        (state): ClassroomState => ({
            ...state,
            isLoadingLessonCode: false,
            started: false,
            startedAt: undefined,
        })
    ),
    on(
        startLessonActions.startLessonSuccess,
        (state, { lessonCode, lesson }): ClassroomState => ({
            ...state,
            isLoadingLessonCode: false,
            activeLesson: { ...lesson },
            liveLessonId: undefined,
        })
    ),
    on(LiveLessonActions.startLiveLessonSuccess, (state, { lesson, lessonCode, liveLessonId })
        : ClassroomState => ({
        ...state,
        isLoadingLessonCode: false,
        activeLesson: { ...lesson },
        liveLessonId,
    })),
    on(
        startLessonActions.lessonStartedAndBeamerWasAutomaticallyOpened,
        (state, { openedOnWindowId }): ClassroomState => ({
            ...state,
            openedOnWindowId,
            started: true,
            startedAt: new Date(),
        })
    ),
    on(
        startLessonActions.lessonStarted_connectionInfoModalWasShown,
        (state): ClassroomState => ({
            ...state,
            openedOnWindowId: undefined,
            started: true,
            startedAt: new Date(),
        })
    ),

    // Failure rollback only. A *completed* stop (local flow or another teacher
    // client) reaches this reducer as the canonical
    // `classroomLifecycleEvents.lessonHasBeenStopped` below — dispatched solely
    // by `ClassroomLifecycleEffects`.
    on(
        stopLessonActions.stopLessonFailed,
        stopLessonActions.postLessonStopToWebApiFailed,
        classroomConnectionActions.connectToClassroomFailed,
        // Consumers (beamer, PtB/live-lesson student, moderator) never see the
        // teacher-scoped stop actions above, so without this handler `started` stayed
        // true on them for the rest of the session — their Y.Doc teardown is bound to
        // logout, not lesson stop. This is the only stop signal every app receives
        // (dispatched centrally in `classroom-consumer.effects.ts`).
        classroomReceiverActions.receivedStopLessonCommand,
        (state): ClassroomState => ({
            ...state,
            started: false,
            paused: false,
            startedAt: undefined,
            activeLesson: undefined, // set after joining the lesson
            liveLessonId: undefined,
            ...NAVIGATION_PREVIEW_DEFAULTS,
        })
    ),
    // NOTE: Synced props (paused, isShowingStudentRegistrationCode, pauseImageUrl)
    // are ONLY set via yjsStateActions.lessonStateMirrored — see handler at the bottom of this reducer.
    on(
        stopLessonActions.postLessonStopToWebApiSuccess,
        (state): ClassroomState => ({
            ...state,
        })
    ),

    on(LiveLessonActions.fetchLiveLessonListSuccess, (state, { liveLessons }): ClassroomState => ({
        ...state,
        liveLessonList: liveLessons,
    })),


    // moved from classroom reducer
    on(classroomConnectionActions.connectToClassroomSuccess, (state, { type, ...activeLesson }): ClassroomState => ({
        ...state,
        activeLesson: { ...activeLesson },
        started: true,
    })),
    on(classroomConnectionActions.connectToClassroomFailed, (state): ClassroomState => ({
        ...state,
        ...initialState,
        activeLesson: undefined,
    })),

    // NOTE: classroomReceiverActions for pause/resume/registrationCode REMOVED —
    // these are now synced via Yjs (yjsStateActions.lessonStateMirrored handler below).
    // Canonical stop reset — the ONE handler for "the lesson has been stopped",
    // whichever path produced it. Also clears the Yjs-mirrored props
    // (isShowingStudentRegistrationCode, isLessonStopPending, pauseImageUrl,
    // paused): the Y.Doc is destroyed on this event, so no mirror update will.
    on(classroomLifecycleEvents.lessonHasBeenStopped, (state): ClassroomState => ({
        ...state,
        isShowingStudentRegistrationCode: false,
        isLessonStopPending: false,
        activeLesson: undefined,
        started: false,
        startedAt: undefined,
        paused: false,
        pauseImageUrl: null,
        liveLessonId: undefined,
        ...NAVIGATION_PREVIEW_DEFAULTS,
    })),

    on(classroomConnectionActions.setNavigationPreviewBasicSlides, (state, { slides, hasParent }): ClassroomState => ({
        ...state,
        navigationPreviewBasicSlideList: slides,
        navigationPreviewHasParent: hasParent,
    })),
    on(classroomConnectionActions.setNavigationPreviewNextSlides, (state, { data }): ClassroomState => ({
        ...state,
        navigationPreviewNextSlideData: data,
    })),

    // Yjs sync: apply remote state changes from other clients.
    //
    // Routing of the SyncedLessonState keys handled here:
    // - paused, pauseImageUrl, isShowingStudentRegistrationCode, isLessonStopPending,
    //   navigationPreviewBasicSlides, navigationPreviewNextSlides → spread into
    //   ClassroomState directly (still consumed via NgRx selectors externally).
    // - activeExtensionApp → handled by the extensionApp NgRx slice (excluded here).
    // - per-layer slide keys (slide / hotspot / viewBox / textboardPage / showingId) →
    //   consumed via `ClassroomFacade` per-layer Signals (showingId: internal to the
    //   drawing service); the NgRx LayerState mirror that used to be maintained here
    //   was vestigial post-Phase-6 and was removed.
    // - dynamicContent → lives in a sibling Y.Array (not Y.Map); consumed via
    //   `ClassroomFacade.dynamicContentAdded$` / `getDynamicContentSnapshot(layer)`.
    on(yjsStateActions.lessonStateMirrored, (state, { changes }): ClassroomState => {
        const {
            activeExtensionApp,
            primarySlide, primarySlideIsAccessedViaHotspot,
            primarySlideViewBox, primarySlideTextboardPage, primarySlideRevealState,
            primarySlideDeactivatedHotspots, primarySlideScrollVideoFrame,
            primarySlideShowingId,
            secondarySlide, secondarySlideIsAccessedViaHotspot,
            secondarySlideViewBox, secondarySlideTextboardPage, secondarySlideRevealState,
            secondarySlideDeactivatedHotspots, secondarySlideScrollVideoFrame,
            secondarySlideShowingId,
            tertiarySlide, tertiarySlideIsAccessedViaHotspot,
            tertiarySlideViewBox, tertiarySlideTextboardPage, tertiarySlideRevealState,
            tertiarySlideDeactivatedHotspots, tertiarySlideScrollVideoFrame,
            tertiarySlideShowingId,
            ...classroomChanges
        } = changes;
        return {
            ...state,
            ...classroomChanges,
        };
    }),
);
